import classNames from "classnames";
import * as React from "react";
import ReactTooltip from "react-tooltip";
import { Style, StyleClass } from "../Styles/CSS";
import * as styles from "./FloatingTools.css";

export function FloatingTools<TInputs>(
    props: TInputs,
    tools: IFloatingTool<TInputs>[]
) {
    return <FloatingToolsView props={props} tools={tools} />;
}

function FloatingToolsView<TInputs>({
    props,
    tools,
}: {
    props: TInputs;
    tools: IFloatingTool<TInputs>[];
}) {
    const containerStyleClass = StyleClass(`floatingToolsContainer`, {
        position: "fixed",
        top: "50%",
        right: "0px",
        transform: "translateY(-50%)",
        fontSize: "16px",
        zIndex: 10,
        padding: "4px",
        borderRadius: "18px",
        background: "rgba(0, 0, 0, 0.18)",
        backdropFilter: "blur(5px)",
        boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
        opacity: 0.62,
        transition: "opacity 180ms ease",
        ":hover": { opacity: 0.86 },
        ":focus-within": { opacity: 0.86 },
    });

    React.useEffect(() => {
        ReactTooltip.rebuild();
    });

    return (
        <div className={containerStyleClass}>
            <span>
                {tools
                    .filter((tool) => tool.isVisible?.(props) ?? true)
                    .map((tool, index) => {
                    const ToRender = () => tool.toRender(props);
                    const isDisabled = tool.isDisabled?.(props) ?? false;
                    const iconClasses = classNames(
                        styles.icon,
                        tool.withClass,
                        { [styles.disabled]: isDisabled }
                    );

                    const tooltipText =
                        typeof tool.tooltip === "function"
                            ? tool.tooltip()
                            : tool.tooltip;

                    return (
                        <div
                            id={`floatingTool_${tool.name}`}
                            className={iconClasses}
                            aria-disabled={isDisabled}
                            onClick={(event) => {
                                if (!isDisabled) {
                                    ActivateFloatingTool(event);
                                }
                            }}
                            {...Style(`floatingToolIcon_${index}`, {
                                color: "rgba(167, 190, 188, 0.76)",
                            })}
                            key={index}
                        >
                            <span data-tip={tooltipText}>
                                <ToRender />
                            </span>
                        </div>
                    );
                })}
            </span>
        </div>
    );
}

function ActivateFloatingTool(event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement;

    if (target.closest("a, button, input, select, textarea")) {
        return;
    }

    event.currentTarget
        .querySelector<HTMLElement>("a, button, [role='button']")
        ?.click();
}

export interface IFloatingTool<TInputs> {
    name: string;
    toRender: (inputs: TInputs) => JSX.Element;
    iconColor: string;
    withClass?: string;
    tooltip: string | StringFunction;
    isDisabled?: (inputs: TInputs) => boolean;
    isVisible?: (inputs: TInputs) => boolean;
}

type StringFunction = () => string;

export function useHasFloatingToolTargets(selector: string) {
    const [hasTargets, setHasTargets] = React.useState(false);

    React.useEffect(() => {
        const updateHasTargets = () =>
            setHasTargets(document.querySelector(selector) !== null);
        const observer = new MutationObserver(updateHasTargets);

        updateHasTargets();
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [selector]);

    return hasTargets;
}
